/**
 * Tab Cronograma — Gantt do projeto.
 * Modelo B: uma barra por TAREFA, agrupada sob cada fase. A fase vira um
 * cabeçalho com a barra-resumo (derivada das tarefas). Tarefa sem data aparece
 * marcada "sem data". Sem libs externas — posicionamento em %.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { tarefasAPI } from '../../../services/inovacaoAPI';

const COR_FASE = {
  planejada: '#95a5a6', em_execucao: '#3498db',
  concluida: '#27ae60', em_risco: '#e67e22', bloqueada: '#e74c3c',
};
const COR_TAREFA = { a_fazer: '#caa83e', fazendo: '#3498db', feito: '#27ae60' };
const LABEL_TAREFA = { a_fazer: 'A fazer', fazendo: 'Fazendo', feito: 'Feito' };

const dia = 86400000;

export default function Cronograma({ projeto, fases }) {
  const [tarefas, setTarefas] = useState(null);

  useEffect(() => {
    tarefasAPI.gantt(projeto.id).then(setTarefas).catch(() => setTarefas([]));
  }, [projeto.id]);

  const porFase = useMemo(() => {
    const m = {};
    (tarefas || []).forEach((t) => { (m[t.fase_id] = m[t.fase_id] || []).push(t); });
    return m;
  }, [tarefas]);

  // Janela do gráfico: engloba projeto + todas as datas de tarefa/fase.
  const { inicio, fim, totalDias, semanas } = useMemo(() => {
    const datas = [];
    if (projeto.data_inicio_planejada) datas.push(new Date(projeto.data_inicio_planejada));
    if (projeto.data_fim_planejada) datas.push(new Date(projeto.data_fim_planejada));
    fases.forEach((f) => {
      if (f.data_inicio_planejada) datas.push(new Date(f.data_inicio_planejada));
      if (f.data_fim_planejada) datas.push(new Date(f.data_fim_planejada));
    });
    (tarefas || []).forEach((t) => {
      if (t.data_inicio_planejada) datas.push(new Date(t.data_inicio_planejada));
      if (t.data_fim_planejada) datas.push(new Date(t.data_fim_planejada));
    });
    if (datas.length === 0) return { inicio: null, fim: null, totalDias: 0, semanas: [] };
    const ini = new Date(Math.min(...datas));
    const f = new Date(Math.max(...datas));
    const total = Math.max(1, Math.round((f - ini) / dia));
    const w = [];
    const d = new Date(ini);
    d.setDate(d.getDate() - d.getDay() + 1);
    while (d <= f) { w.push(new Date(d)); d.setDate(d.getDate() + 7); }
    return { inicio: ini, fim: f, totalDias: total, semanas: w };
  }, [projeto, fases, tarefas]);

  if (tarefas === null) return <div style={cardStyle}><p style={{ color: '#bdc3c7' }}>Carregando cronograma…</p></div>;
  if (!inicio || !fim) {
    return <div style={cardStyle}><p style={{ color: '#bdc3c7' }}>Sem datas para montar o cronograma. Defina datas nas tarefas ou fases.</p></div>;
  }

  const COL = 260;
  const barra = (di, df) => {
    if (!di || !df) return null;
    const a = new Date(di), b = new Date(df);
    const left = Math.max(0, (a - inicio) / dia) / totalDias * 100;
    const width = Math.max(1, ((b - a) / dia + 1) / totalDias * 100);
    return { left, width };
  };
  const hoje = new Date();
  const hojeOffset = Math.max(0, Math.min(totalDias, (hoje - inicio) / dia));

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 4px', color: '#2c3e50' }}>Cronograma: {projeto.nome}</h3>
      <p style={{ margin: '0 0 18px', color: '#95a5a6', fontSize: '0.82rem' }}>Uma barra por tarefa, agrupada sob a fase.</p>

      {/* Cabeçalho de semanas */}
      <div style={{ display: 'flex', marginBottom: 8, paddingLeft: COL }}>
        <div style={{ flex: 1, position: 'relative', height: 18 }}>
          {semanas.map((s, i) => {
            const leftPct = (s - inicio) / dia / totalDias * 100;
            return (
              <div key={i} style={{ position: 'absolute', left: `${leftPct}%`, fontSize: '0.68rem',
                                    color: '#95a5a6', borderLeft: '1px solid #ecf0f1', paddingLeft: 4, top: 0 }}>
                {s.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        {fases.map((f) => {
          const ts = porFase[f.id] || [];
          const bf = barra(f.data_inicio_planejada, f.data_fim_planejada);
          return (
            <React.Fragment key={f.id}>
              {/* Cabeçalho da fase com barra-resumo (derivada) */}
              <div style={{ display: 'flex', height: 30, alignItems: 'center', borderBottom: '1px solid #f4f6f8' }}>
                <div style={{ width: COL, paddingRight: 12, fontSize: '0.85rem', fontWeight: 600, color: '#2c3e50',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.nome}>
                  <span style={{ color: '#95a5a6', marginRight: 6 }}>{f.ordem}.</span>
                  {f.eh_buffer && '🔸 '}{f.nome}
                </div>
                <div style={{ flex: 1, position: 'relative', height: 30 }}>
                  {bf && (
                    <div title={`${f.nome} · ${parseFloat(f.horas_estimadas || 0)}h`}
                         style={{ position: 'absolute', left: `${bf.left}%`, width: `${bf.width}%`, top: 11, height: 8,
                                  background: COR_FASE[f.estado] || '#95a5a6', opacity: 0.35, borderRadius: 4 }} />
                  )}
                </div>
              </div>

              {/* Tarefas da fase */}
              {ts.map((t) => {
                const bt = barra(t.data_inicio_planejada, t.data_fim_planejada);
                const cor = COR_TAREFA[t.estado] || '#95a5a6';
                return (
                  <div key={t.id} style={{ display: 'flex', height: 28, alignItems: 'center', borderBottom: '1px solid #f8fafb' }}>
                    <div style={{ width: COL, paddingLeft: 22, paddingRight: 12, fontSize: '0.8rem', color: '#566573',
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                         title={t.titulo}>
                      {t.predecessoras_ids?.length > 0 && <span style={{ color: '#8e44ad' }} title="Tem predecessora">↳ </span>}
                      {t.eh_desvio && <span style={{ color: '#e67e22', fontWeight: 700 }} title="Desvio do plano">⚠ </span>}
                      {t.titulo}
                    </div>
                    <div style={{ flex: 1, position: 'relative', height: 28 }}>
                      {bt ? (
                        <div title={`${t.titulo} · ${LABEL_TAREFA[t.estado] || t.estado} · ${parseFloat(t.horas_estimadas || 0)}h · ${t.responsavel_nome || 'sem responsável'}`}
                             style={{ position: 'absolute', left: `${bt.left}%`, width: `${bt.width}%`, top: 7, height: 14,
                                      background: cor, borderRadius: 4, display: 'flex', alignItems: 'center',
                                      color: '#fff', fontSize: '0.66rem', padding: '0 6px', overflow: 'hidden',
                                      textDecoration: t.estado === 'feito' ? 'line-through' : 'none', opacity: t.estado === 'feito' ? 0.75 : 1 }}>
                          {t.responsavel_nome?.split(' ')[0] || ''}
                        </div>
                      ) : (
                        <span style={{ fontSize: '0.7rem', color: '#cbd5d8', lineHeight: '28px' }}>sem data</span>
                      )}
                    </div>
                  </div>
                );
              })}
              {ts.length === 0 && !bf && (
                <div style={{ display: 'flex', height: 24, alignItems: 'center' }}>
                  <div style={{ width: COL, paddingLeft: 22, fontSize: '0.75rem', color: '#cbd5d8' }}>sem tarefas / sem datas</div>
                </div>
              )}
            </React.Fragment>
          );
        })}

        {/* Linha "hoje" */}
        <div style={{ position: 'absolute', top: 0, bottom: 0,
                      left: `calc(${COL}px + (100% - ${COL}px) * ${hojeOffset / totalDias})`,
                      width: 2, background: '#e74c3c', opacity: 0.5, pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', top: -8, left: -12, background: '#e74c3c', color: '#fff',
                        padding: '1px 6px', fontSize: '0.62rem', borderRadius: 3, whiteSpace: 'nowrap' }}>hoje</div>
        </div>
      </div>

      {/* Legenda */}
      <div style={{ marginTop: 22, display: 'flex', gap: 18, fontSize: '0.78rem', color: '#7f8c8d', flexWrap: 'wrap' }}>
        {Object.entries(COR_TAREFA).map(([k, v]) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 14, height: 10, background: v, borderRadius: 2 }} /> {LABEL_TAREFA[k]}
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 14, height: 8, background: '#95a5a6', opacity: 0.35, borderRadius: 2 }} /> fase (resumo)
        </div>
      </div>
    </div>
  );
}

const cardStyle = { background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' };
